/**
 * Bottom-nav icons — LARGE two-tone filled glyphs (Duolingo-style).
 *
 * Each glyph paints itself in its tab's own brand color pair:
 *   • main silhouette → `var(--tab-color)`
 *   • detail layer    → white or `var(--tab-color-dark)`
 *
 * Icons are ALWAYS full-color — active and inactive alike. The inactive
 * state is expressed by the wrapper (`.fluent-bottomnav__icon`) via
 * opacity + slight grayscale, never by recoloring the glyph.
 *
 * Chunky-rounded, filled only — no strokes. Sized via `font-size` on
 * `.fluent-bottomnav__icon`. ViewBox is 28, target render ~30–32px.
 */

const SVG_PROPS = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 28 28',
  'aria-hidden': true,
} as const;

const MAIN = 'var(--tab-color)';
const DARK = 'var(--tab-color-dark)';
const WHITE = '#fff';

export function HomeIcon() {
  return (
    <svg {...SVG_PROPS}>
      {/* house silhouette */}
      <path
        fill={MAIN}
        d="M12.9 2.6 a1.7 1.7 0 0 1 2.2 0 L25.5 11.3 a1.7 1.7 0 0 1 0.6 1.3 V23.4 A2.6 2.6 0 0 1 23.5 26 H4.5 A2.6 2.6 0 0 1 1.9 23.4 V12.6 a1.7 1.7 0 0 1 0.6 -1.3 Z"
      />
      {/* white door */}
      <path
        fill={WHITE}
        d="M10.8 26 V17.6 a1.7 1.7 0 0 1 1.7 -1.7 h3 a1.7 1.7 0 0 1 1.7 1.7 V26 Z"
      />
    </svg>
  );
}

export function BookIcon() {
  return (
    <svg {...SVG_PROPS}>
      {/* back page — dark */}
      <path
        fill={DARK}
        d="M24.5 5.5 H16 a1.5 1.5 0 0 0 -1.5 1.5 v17 a1 1 0 0 0 1.5 0.85 a8 8 0 0 1 4 -1.1 H24 a1.5 1.5 0 0 0 1.5 -1.5 V7 a1.5 1.5 0 0 0 -1 -1.5 Z"
      />
      {/* front page — main */}
      <path
        fill={MAIN}
        d="M3.5 5.5 H12 a1.5 1.5 0 0 1 1.5 1.5 v17 a1 1 0 0 1 -1.5 0.85 a8 8 0 0 0 -4 -1.1 H4 a1.5 1.5 0 0 1 -1.5 -1.5 V7 a1.5 1.5 0 0 1 1 -1.5 Z"
      />
      {/* white text lines on the front page */}
      <rect x="5.5" y="10" width="5.5" height="2.2" rx="1.1" fill={WHITE} />
      <rect x="5.5" y="14.5" width="4" height="2.2" rx="1.1" fill={WHITE} />
    </svg>
  );
}

export function CardsIcon() {
  return (
    <svg {...SVG_PROPS}>
      {/* back card, tilted — dark */}
      <rect
        x="10"
        y="2.5"
        width="14"
        height="18"
        rx="2.6"
        transform="rotate(10 17 11.5)"
        fill={DARK}
      />
      {/* front card — main */}
      <rect x="3.5" y="7" width="14.5" height="18.5" rx="2.6" fill={MAIN} />
      {/* two white text lines */}
      <rect x="6.5" y="12.5" width="8.5" height="2.4" rx="1.2" fill={WHITE} />
      <rect x="6.5" y="17.5" width="5.5" height="2.4" rx="1.2" fill={WHITE} />
    </svg>
  );
}

/** ტესტი tab — an exam paper on a clipboard with two ticked answers. */
export function TestIcon() {
  return (
    <svg {...SVG_PROPS}>
      {/* board — main */}
      <rect x="3.8" y="3.4" width="20.4" height="23.2" rx="3.4" fill={MAIN} />
      {/* paper — white */}
      <rect x="6.8" y="6.6" width="14.4" height="17.2" rx="1.8" fill={WHITE} />
      {/* clip — dark */}
      <rect x="9.6" y="1.4" width="8.8" height="4.8" rx="1.8" fill={DARK} />
      {/* two ticks — main */}
      <path fill={MAIN} d="M8.6 11.6 l1.4 -1.4 1.2 1.2 2.7 -2.7 1.4 1.4 -4.1 4.1 Z" />
      <path fill={MAIN} d="M8.6 17.4 l1.4 -1.4 1.2 1.2 2.7 -2.7 1.4 1.4 -4.1 4.1 Z" />
      {/* answer lines — dark */}
      <rect x="15.6" y="10.4" width="3.8" height="2.2" rx="1.1" fill={DARK} />
      <rect x="15.6" y="16.2" width="3.8" height="2.2" rx="1.1" fill={DARK} />
    </svg>
  );
}

/** ფრაზები tab — two speech bubbles: the reply behind (dark), the phrase in
 *  front (main) with a tail and three white "talking" dots. */
export function PhrasesIcon() {
  return (
    <svg {...SVG_PROPS}>
      {/* back bubble, tail bottom-right — dark */}
      <path
        fill={DARK}
        d="M10 4 h12 a4 4 0 0 1 4 4 v7 a4 4 0 0 1 -4 4 h-1 v3.5 l-4 -3.5 h-7 a4 4 0 0 1 -4 -4 V8 a4 4 0 0 1 4 -4 Z"
      />
      {/* front bubble, tail bottom-left — main */}
      <path
        fill={MAIN}
        d="M5 9 h12 a4 4 0 0 1 4 4 v7 a4 4 0 0 1 -4 4 h-7 l-4.5 3.5 V24 H5 a4 4 0 0 1 -4 -4 v-7 a4 4 0 0 1 4 -4 Z"
      />
      {/* three dots — white */}
      <circle cx="7" cy="16.5" r="1.7" fill={WHITE} />
      <circle cx="11" cy="16.5" r="1.7" fill={WHITE} />
      <circle cx="15" cy="16.5" r="1.7" fill={WHITE} />
    </svg>
  );
}

/** No longer in the tab bar (Missions left it for ფრაზები, 2026-10-18);
 *  kept for any future award surface. */
export function TrophyIcon() {
  return (
    <svg {...SVG_PROPS}>
      {/* handles — main */}
      <path
        fill={MAIN}
        d="M6.5 5.5 V8.3 H4.9 V10 a3.6 3.6 0 0 0 2.4 3.4 V16.4 A6.6 6.6 0 0 1 2.4 10 V7.3 A1.8 1.8 0 0 1 4.2 5.5 Z"
      />
      <path
        fill={MAIN}
        d="M21.5 5.5 H23.8 A1.8 1.8 0 0 1 25.6 7.3 V10 a6.6 6.6 0 0 1 -4.9 6.4 V13.4 A3.6 3.6 0 0 0 23.1 10 V8.3 H21.5 Z"
      />
      {/* cup — main */}
      <path
        fill={MAIN}
        d="M7.2 2.8 H20.8 a1.2 1.2 0 0 1 1.2 1.2 V10.4 a8 8 0 0 1 -16 0 V4 a1.2 1.2 0 0 1 1.2 -1.2 Z"
      />
      {/* white shine on the cup */}
      <rect x="9.5" y="5.3" width="2.6" height="6" rx="1.3" fill={WHITE} />
      {/* stem — dark */}
      <path fill={DARK} d="M11.2 17.2 h5.6 l-0.8 5.6 h-4 Z" />
      {/* base — dark */}
      <rect x="7" y="23.6" width="14" height="3.4" rx="1.7" fill={DARK} />
    </svg>
  );
}

export function UserIcon() {
  return (
    <svg {...SVG_PROPS}>
      {/* head — dark */}
      <circle cx="14" cy="8.2" r="6.2" fill={DARK} />
      {/* body — main */}
      <path
        fill={MAIN}
        d="M14 15.8 a10.4 10.4 0 0 1 10.3 9.4 a1.7 1.7 0 0 1 -1.7 1.8 H5.4 a1.7 1.7 0 0 1 -1.7 -1.8 A10.4 10.4 0 0 1 14 15.8 Z"
      />
    </svg>
  );
}
